import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useSiteConfig } from "@/context/SiteConfigContext";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const tokenAuthStarted = useRef(false);
  const { authEnabled, localAdminMode, configError, loading: siteConfigLoading } = useSiteConfig();

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem("dl_token");
    if (!token && !localAdminMode) { setLoading(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("dl_token");
    } finally {
      setLoading(false);
    }
  }, [localAdminMode]);

  useEffect(() => {
    const hasToken = Boolean(localStorage.getItem("dl_token"));
    if (siteConfigLoading) {
      // A returning user can validate the existing token in parallel with the
      // public site-config request instead of paying for two serial round trips.
      if (hasToken && !tokenAuthStarted.current) {
        tokenAuthStarted.current = true;
        loadUser();
      }
      return;
    }
    if (configError) {
      setLoading(false);
      return;
    }
    if (!authEnabled) {
      localStorage.removeItem("dl_token");
      setUser(null);
      setLoading(false);
      return;
    }
    if (!hasToken || !tokenAuthStarted.current) {
      tokenAuthStarted.current = true;
      loadUser();
    }
  }, [authEnabled, configError, loadUser, siteConfigLoading]);

  const logout = () => {
    localStorage.removeItem("dl_token");
    setUser(null);
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, loadUser, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
